import { addSmsLog, type SmsChannel } from "./smsLogsStore";
import { getSmsSettings } from "./smsSettingsStore";
import {
  getSmsTemplates,
  isSmsTemplateEnabled,
  type SmsTemplateKey,
} from "./smsTemplates";

interface SendSmsByTemplateParams {
  templateKey: SmsTemplateKey;
  channel: SmsChannel;
  eventType: string;
  phone: string;
  customerId?: number;
  userOptIn?: boolean | null;
  variables?: Record<string, string | number | null | undefined>;
}

interface SendManualSmsParams {
  channel: SmsChannel;
  eventType: string;
  phone: string;
  customerId?: number;
  userOptIn?: boolean | null;
  message: string;
}

function normalizePhone(value?: string) {
  return String(value ?? "").replace(/\D/g, "");
}

function trimText(value?: string | number | null) {
  return String(value ?? "").trim();
}

function maskPhone(value: string) {
  const normalized = normalizePhone(value);
  if (normalized.length < 4) return normalized;
  return `${normalized.slice(0, 3)}***${normalized.slice(-2)}`;
}

function renderTemplate(
  template: string,
  variables?: Record<string, string | number | null | undefined>
) {
  let output = template;

  Object.entries(variables ?? {}).forEach(([key, value]) => {
    output = output.replaceAll(`{{${key}}}`, trimText(value));
  });

  return output.replace(/\s+/g, " ").trim();
}

function resolvePermission(
  channel: SmsChannel,
  userOptIn?: boolean | null
): { allowed: boolean; reason?: string } {
  const settings = getSmsSettings();

  if (!settings.isActive) {
    return { allowed: false, reason: "SMS sistemi kapalı." };
  }

  if (channel === "operational" && !settings.operationalEnabled) {
    return { allowed: false, reason: "Operasyonel SMS kapalı." };
  }

  if (channel === "marketing" && !settings.marketingEnabled) {
    return { allowed: false, reason: "Reklam / bilgilendirme SMS kapalı." };
  }

  if ((channel === "operational" || channel === "marketing") && userOptIn !== true) {
    return { allowed: false, reason: "Müşteri SMS izni vermemiş." };
  }

  return { allowed: true };
}

export async function sendManualSms(params: SendManualSmsParams) {
  const phone = normalizePhone(params.phone);
  const message = trimText(params.message);

  if (!phone) {
    return addSmsLog({
      phone: params.phone,
      customerId: params.customerId,
      channel: params.channel,
      eventType: params.eventType,
      message: message || "Telefon numarası bulunamadı.",
      status: "failed",
      providerResponse: "Telefon numarası yok.",
    });
  }

  if (!message) {
    return addSmsLog({
      phone,
      customerId: params.customerId,
      channel: params.channel,
      eventType: params.eventType,
      message: "Boş mesaj",
      status: "failed",
      providerResponse: "Mesaj içeriği boş.",
    });
  }

  const permission = resolvePermission(params.channel, params.userOptIn);

  if (!permission.allowed) {
    return addSmsLog({
      phone,
      customerId: params.customerId,
      channel: params.channel,
      eventType: params.eventType,
      message,
      status: "disabled",
      providerResponse: permission.reason,
    });
  }

  const settings = getSmsSettings();

  return addSmsLog({
    phone,
    customerId: params.customerId,
    channel: params.channel,
    eventType: params.eventType,
    message,
    status: "sent",
    providerResponse: `Mock NetGSM gönderimi başarılı (${settings.senderTitle || "BASLIK_YOK"} / ${maskPhone(phone)})`,
    sentAt: new Date().toISOString(),
  });
}

export async function sendSmsByTemplate(params: SendSmsByTemplateParams) {
  const templates = getSmsTemplates();
  const template = templates[params.templateKey];
  const message = renderTemplate(template, params.variables);

  // Otomatik şablon SMS'lerinde sistem kapalıysa sessiz kal.
  // Bu sayede müşteri/taşıma/ödeme akışları SMS kapalıyken log şişirmez.
  if (!isSmsTemplateEnabled(params.templateKey)) {
    return null;
  }

  const permission = resolvePermission(params.channel, params.userOptIn);
  if (!permission.allowed) {
    return null;
  }

  return sendManualSms({
    channel: params.channel,
    eventType: params.eventType,
    phone: params.phone,
    customerId: params.customerId,
    userOptIn: params.userOptIn,
    message,
  });
}
